/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  BuilderNavigationService,
  BuilderUserContext,
  CommonActions,
  CommonFlowState,
  ListActionPipelines,
} from '@gh/builder-shared';
import {
  AelEvents,
  BuilderPermission,
  BuilderPermissionSet,
  getAelEventsArray,
  SalesFolderStatus,
} from '@gh/builder-shared-data';
import { rendererIconByName, truthyCellRenderer } from '@gh/common-internal-shared';
import { AppRouter, DateTimeTypes, getRouteInitData } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { DialogService, STYLES_FLEX_COLUMN_LAYOUT, WaitingService } from '@gh/core-ui';
import {
  buildDateTimeColumn,
  GridDefinitionFactory,
  LayoutBasedGridDefinition,
} from '@gh/core-ui-grid';
import { assign, concat } from 'lodash';
import { RfqBusinessService } from '../../../resources/rfq-business.service';
import { RfqSiteList, RfqSiteListItem } from '../../../resources/rfq.model';
import { RfqTabContainerComponent } from '../rfq-tab-container.component';

@Component({
  standalone: false,
  selector: 'gh-lead-details-rfq-site-list-tab',
  templateUrl: 'rfq-site-list-tab.component.html',
  styles: [STYLES_FLEX_COLUMN_LAYOUT],
})
export class RfqSitesTabComponent implements OnInit {
  gridDefinition: LayoutBasedGridDefinition;
  sitesList: RfqSiteList;
  isEditMode: boolean;

  constructor(private route: ActivatedRoute,
              private gridDefinitionFactory: GridDefinitionFactory,
              private navigationService: BuilderNavigationService,
              private pipelines: ListActionPipelines,
              private commonActions: CommonActions,
              private businessService: RfqBusinessService,
              private waitingService: WaitingService,
              private router: AppRouter,
              private userContext: BuilderUserContext,
              private parentContainer: RfqTabContainerComponent,
              private dialogService: DialogService) {

    this.gridDefinition = this.gridDefinitionFactory.buildLayoutBased({
      inspectedDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
      isInspected: {
        cellRenderer: truthyCellRenderer(rendererIconByName('done')),
      },
    },                                                                {
      getRowId: (params) => String(params.data.id)
    });
  }

  ngOnInit(): void {
    const { gridLayout, sitesList } = getRouteInitData(this.route);

    this.isEditMode = (this.parentContainer.salesFolder.status === SalesFolderStatus.Active) &&
      this.userContext.hasPermission(BuilderPermission.Sales.Leads.EDIT);

    assign(this, { sitesList });

    this.gridDefinition.bind(gridLayout);

  }

  goToAel(siteId: ID): void {
    this.navigationService.goToAel({
      label: 'lbl_sites_list',
      entityType: siteId ? 'SITE' : 'SALES_FOLDER',
      entityId: siteId ? siteId : this.sitesList.salesFolderId,
      eventTypeIds: concat(getAelEventsArray(AelEvents.SalesFolders.Site), getAelEventsArray(AelEvents.Site)),
    });
  }

  goToRfqAel(): void {
    this.navigationService.goToAel({
      label: 'lbl_sites_list',
      entityId: this.sitesList.salesFolderId,
      entityType: 'SALES_FOLDER',
    });
  }

  onCreate(): void {
    const sfId = this.sitesList.salesFolderId;

    this.router.navigateToChild([`/sales/sales-folders/${sfId}/site/new`]);
  }

  onOpen(site: RfqSiteListItem): void {
    if (this.userContext.hasPermission(BuilderPermissionSet.Sales.Leads.EDIT_VIEW)) {
      const sfId = this.sitesList.salesFolderId;

      this.router.navigateToChild([`/sales/sales-folders/${sfId}/site/${site.id}`]);
    }
  }

  onDelete(site: RfqSiteListItem): void {
    const flow = this.pipelines.toggleItem({
      question: { id: 'msg_site_delete', params: { name: site.name } },
      validate: this.commonActions.validate((state: CommonFlowState<any>) =>
        this.businessService.siteCheckUsage(this.sitesList.salesFolderId, state.value)),
      execute: this.commonActions.execute((state: CommonFlowState<any>) =>
        this.businessService.removeSite(this.sitesList.salesFolderId, site.id)),
      finish: () => this.loadData(),
    });

    flow.run({
      value: site,
    });
  }

  private loadData(): void {
    this.businessService.getSiteData(this.sitesList.salesFolderId).pipe(
      this.waitingService.operator())
      .subscribe((sitesList: RfqSiteList) => this.sitesList = sitesList);
  }
}
